import { useState } from 'react';
import { IconWarn } from '../components/auth/AuthBits';
import { partnerLogin } from '../api';
import '../styles/page-login.css';
import Seo from '../components/Seo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function PartnerLoginPage() {

  const [done, setDone] = useState(false);
  const [v, setV] = useState({ email: '', password: '' });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);

  const check = (name, val) => {
    if (name === 'email') {
      if (!val.trim()) return 'Enter your partner email.';
      if (!EMAIL_RE.test(val.trim())) return 'That email address looks incomplete.';
    }
    if (name === 'password' && !val) return 'Enter your password.';
    return '';
  };

  const set = (name) => (e) => {
    const val = e.target.value;
    setV((s) => ({ ...s, [name]: val }));
    if (err[name]) setErr((s) => ({ ...s, [name]: check(name, val) }));
  };
  /* validate on blur, not on every keystroke */
  const blur = (name) => (e) => setErr((s) => ({ ...s, [name]: check(name, e.target.value) }));

  const submit = (e) => {
    e.preventDefault();
    const next = { email: check('email', v.email), password: check('password', v.password) };
    setErr(next);
    const bad = Object.keys(next).find((k) => next[k]);
    if (bad) {
      document.getElementById(bad === 'email' ? 'pl-email' : 'pl-password')?.focus();
      return;
    }
    setBusy(true);
    setErr((s) => ({ ...s, form: '' }));
    partnerLogin({ email: v.email, password: v.password })
      .then(() => {
        setDone(true);
        /* full navigation, not client-side: the partner portal lives outside this module */
        window.location.assign('/partner');
      })
      .catch((e2) => {
        const s = e2?.status;
        setErr((prev) => ({
          ...prev,
          form:
            s === 401
              ? 'That email and password don’t match.'
              : s === 403
                ? 'That account doesn’t have partner portal access.'
                : s === 404
                  ? 'We couldn’t find a partner account for that email.'
                  : s === 429
                    ? 'Too many attempts. Wait a few minutes and try again.'
                    : e2?.message || 'Something went wrong. Please try again.',
        }));
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <Seo title="Partner Portal Sign In | TeamGrid™" description="Sign in to the TeamGrid partner portal to track recurring commission, audit payouts in rupees straight from billing, and manage your client accounts." noindex />
<div className="auth-wrap">
  <div className="auth-card">
    <img className="amark" src="/assets/mark-teamgrid.svg" alt="TeamGrid" />

    {!done && (
      <>
        <h1>Partner portal</h1>
        <p className="asub">Sign in to your commission dashboard</p>
        <form data-fake onSubmit={submit} noValidate>
          <label htmlFor="pl-email">Partner email</label>
          <input
            id="pl-email"
            required
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck="false"
            placeholder="you@partner.com"
            value={v.email}
            onChange={set('email')}
            onBlur={blur('email')}
            style={{borderColor: err.email ? '#E0483D' : undefined}}
            aria-invalid={!!err.email}
            aria-describedby={err.email ? 'pl-email-err' : undefined}
          />
          {err.email && <p className="ax-err" id="pl-email-err" role="alert"><IconWarn />{err.email}</p>}

          <label htmlFor="pl-password">Password</label>
          <input
            id="pl-password"
            required
            type="password"
            autoComplete="current-password"
            placeholder="••••••••••"
            value={v.password}
            onChange={set('password')}
            onBlur={blur('password')}
            style={{borderColor: err.password ? '#E0483D' : undefined}}
            aria-invalid={!!err.password}
            aria-describedby={err.password ? 'pl-password-err' : undefined}
          />
          {err.password && <p className="ax-err" id="pl-password-err" role="alert"><IconWarn />{err.password}</p>}

          {err.form && <p className="ax-err" role="alert" style={{ marginTop: '16px' }}><IconWarn />{err.form}</p>}

          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in to portal'}
          </button>
        </form>

        <ul style={{display: 'flex', flexDirection: 'column', gap: '10px', margin: '22px 0 0'}}>
          <li style={{display: 'flex', gap: '9px', fontSize: '13px', lineHeight: '19px', color: 'var(--ink-soft)'}}>
            <svg aria-hidden="true" className="chk" viewBox="0 0 16 16" style={{width: '14px', height: '14px', flex: '0 0 auto', marginTop: '3px'}}><path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
            <span><b style={{color: 'var(--ink-strong)'}}>Recurring, not one-time.</b> You earn every billing cycle, for as long as the client stays.</span>
          </li>
          <li style={{display: 'flex', gap: '9px', fontSize: '13px', lineHeight: '19px', color: 'var(--ink-soft)'}}>
            <svg aria-hidden="true" className="chk" viewBox="0 0 16 16" style={{width: '14px', height: '14px', flex: '0 0 auto', marginTop: '3px'}}><path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
            <span><b style={{color: 'var(--ink-strong)'}}>Payouts you can audit.</b> Exact amounts in ₹, pulled straight from billing.</span>
          </li>
          <li style={{display: 'flex', gap: '9px', fontSize: '13px', lineHeight: '19px', color: 'var(--ink-soft)'}}>
            <svg aria-hidden="true" className="chk" viewBox="0 0 16 16" style={{width: '14px', height: '14px', flex: '0 0 auto', marginTop: '3px'}}><path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
            <span><b style={{color: 'var(--ink-strong)'}}>Nothing to run.</b> We handle the product. You bring the clients.</span>
          </li>
        </ul>
      </>
    )}

    {done && (
      <div className="oksplash show">
        <div className="okring">
          <svg aria-hidden="true" width="28" height="28" viewBox="0 0 16 16"><path fill="none" stroke="#1d7a43" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
        </div>
        <h3 style={{fontSize: '18px', fontWeight: '800', color: 'var(--ink-strong)'}}>Demo build — partner sign-in is simulated.</h3>
        <p style={{fontSize: '13px', color: 'var(--ink-soft)', marginTop: '6px'}}>In production this opens your live commission dashboard.</p>
      </div>
    )}

    <p style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontSize: '11.5px', fontWeight: '700', letterSpacing: '.4px', color: 'var(--ink-faint)', marginTop: '20px'}}>
      <svg aria-hidden="true" width="13" height="13" viewBox="0 0 20 20" fill="none"><rect x="4" y="9" width="12" height="8" rx="2" stroke="currentColor" strokeWidth="1.9" /><path d="M7 9V7a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.9" /></svg>
      Encrypted, access-isolated partner login
    </p>

    <p className="aswitch">Not a partner yet? <a href="/partners">See the partner program</a></p>
    <p className="aswitch" style={{marginTop: '6px'}}>Looking for your own workspace? <a href="/login">Standard sign in</a></p>
  </div>
</div>
    </>
  );
}
